"use client";

import React from "react";
import Image from "next/image";
import { motion, type Variants, type Transition } from "framer-motion";
import { ArrowRight, FileText } from "lucide-react";
import type { Product } from "@/data/products-page";

const EASE: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

const revealTransition = (delay: number): Transition => ({
  duration: 0.65,
  delay,
  ease: EASE,
});

const makeMobileVariants = (delay: number): Variants => ({
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: revealTransition(delay) },
});

const V = Array.from({ length: 8 }, (_, i) => makeMobileVariants(i * 0.08));

const mobileCardV: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

interface ProductCardProps {
  product: Product;
  index: number;
  isMobile: boolean;
  onEnquire: (product: Product) => void;
}

const ProductCard = React.forwardRef<HTMLDivElement, ProductCardProps>(
  ({ product, index, isMobile, onEnquire }, ref) => {
    const { id, title, category, description, image, features, accent, bg, textColor } =
      product;

    const num = String(id).padStart(2, "0");
    const viewport = { once: true, margin: "-60px" } as const;

    // ── Inside ProductCard.tsx ──

const ctaBlock = (
  <div className="flex flex-wrap items-center gap-5" data-animate="cta">
    <motion.button
      className="
        group relative inline-flex items-center overflow-hidden rounded-md 
        px-7 py-3.5 text-xs font-bold tracking-[0.12em] uppercase 
        text-white shadow-lg transition-all duration-300 
        hover:shadow-xl hover:brightness-125 active:scale-95
      "
      
      // ✅ CHANGE HERE: Hardcode the color. 
      // Use #0a0a0a for Universal Black, or any brand color (e.g., #2563EB for Blue)
      style={{ backgroundColor: '#0a0a0a' }} 
      
      // ✅ Keep this attribute so GSAP can find it for the animation
      data-gsap-btn 
      
      onClick={() => onEnquire(product)}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.97 }}
    >
      {/* Subtle shine effect on hover */}
      <span className="absolute inset-0 translate-x-[-100%] skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-[100%]" />
      
      Enquire Now
      <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-1" />
    </motion.button>

    {/* View Specs Button */}
    <motion.button
      className="group inline-flex items-center text-xs font-semibold tracking-[0.12em] uppercase underline underline-offset-4 opacity-40 transition-all hover:opacity-80"
      style={{ color: textColor }}
      whileHover={{ x: 3 }}
    >
      <FileText size={13} className="mr-1.5 transition-transform group-hover:-rotate-12" />
      View Specs
    </motion.button>
  </div>
);

    const metaRow = (
      <div className="mb-3 flex items-center gap-3 sm:mb-4" data-animate="meta">
        <span
          className="text-xs font-bold tracking-[0.25em] tabular-nums opacity-35"
          style={{ color: textColor }}
        >
          {num}
        </span>
        <div className="h-px w-8 shrink-0 opacity-40" style={{ backgroundColor: accent }} />
        <span
          className="whitespace-nowrap text-xs font-bold tracking-[0.2em] uppercase opacity-50"
          style={{ color: textColor }}
        >
          {category}
        </span>
      </div>
    );

    const featureList = (
      <ul className="mb-6 flex flex-col gap-2 sm:mb-8 lg:mb-10" data-animate="features">
        {features.map((f, fi) => (
          <li
            key={f}
            className="flex items-center gap-3 text-sm font-medium"
            style={{ color: textColor }}
            data-animate="feature"
            data-feature-index={fi}
          >
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: accent }}
            />
            {f}
          </li>
        ))}
      </ul>
    );

    /* ── MOBILE ── */
    if (isMobile) {
      return (
        <motion.article
          ref={ref}
          className="grid h-auto min-h-[100svh] w-full grid-cols-1"
          style={{ backgroundColor: bg, color: textColor }}
          variants={mobileCardV}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          aria-label={`Product ${num}: ${title}`}
        >
          <div className="order-1 flex h-[44vh] items-center justify-center overflow-hidden p-6">
            <motion.div
              className="relative h-full w-full overflow-hidden rounded-xl"
              variants={V[0]}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <Image
                src={image}
                alt={`${title} — ${category}`}
                fill
                className="object-cover"
                sizes="100vw"
                priority={index === 0}
                quality={85}
              />
            </motion.div>
          </div>

          <div className="order-2 flex flex-col justify-center px-6 py-10">
            <motion.div variants={V[1]} initial="hidden" whileInView="visible" viewport={viewport}>
              {metaRow}
            </motion.div>

            <motion.h2
              className="mb-4 text-[clamp(2rem,9vw,2.6rem)] font-extrabold leading-[1.02] tracking-tight"
              style={{ color: textColor }}
              variants={V[2]}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              {title}
            </motion.h2>

            <motion.p
              className="mb-6 max-w-[44ch] text-sm leading-[1.7] opacity-65"
              style={{ color: textColor }}
              variants={V[3]}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              {description}
            </motion.p>

            <motion.div variants={V[4]} initial="hidden" whileInView="visible" viewport={viewport}>
              {featureList}
            </motion.div>

            <motion.div variants={V[5]} initial="hidden" whileInView="visible" viewport={viewport}>
              {ctaBlock}
            </motion.div>
          </div>
        </motion.article>
      );
    }

    /* ── DESKTOP ── */
    return (
      <article
        ref={ref}
        className="relative grid h-[100vh] w-full grid-cols-2 items-center overflow-hidden [backface-visibility:hidden]"
        style={{ backgroundColor: bg, color: textColor }}
        aria-label={`Product ${num}: ${title}`}
        data-card-index={index}
      >
        <span
          className="pointer-events-none absolute bottom-8 right-[6vw] select-none text-[clamp(3rem,6vw,7rem)] font-black leading-none tracking-tighter opacity-[0.04]"
          style={{ color: textColor }}
        >
          {num}
        </span>

        {/* Left: content — scrollable fallback if a viewport is too short */}
        <div className="h-full max-h-[100vh] overflow-y-auto px-[clamp(1.5rem,5vw,7vw)] py-[clamp(2rem,4vw,5rem)] pr-[clamp(1.5rem,4vw,5vw)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex min-h-full flex-col justify-center">
            {metaRow}

            <div className="mb-3 overflow-hidden sm:mb-5 lg:mb-6">
              <h2
                className="text-[clamp(2rem,3.6vw,5.5rem)] font-extrabold leading-[1.02] tracking-tight break-words"
                style={{ color: textColor }}
                data-animate="title"
              >
                {title}
              </h2>
            </div>

            <p
              className="mb-4 max-w-[44ch] text-[clamp(0.82rem,1vw,1.05rem)] leading-[1.7] opacity-65 sm:mb-6 lg:mb-8"
              style={{ color: textColor }}
              data-animate="desc"
            >
              {description}
            </p>

            {featureList}

            {ctaBlock}

            {index === 0 && (
              <div className="absolute bottom-6 left-0 flex items-center gap-2.5 opacity-30">
                <div className="h-px w-7" style={{ backgroundColor: textColor }} />
                <span
                  className="whitespace-nowrap text-[0.65rem] font-semibold tracking-[0.2em] uppercase"
                  style={{ color: textColor }}
                >
                  Scroll to explore
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right: image */}
        <div className="flex h-full w-full items-center justify-center overflow-hidden py-[clamp(1.5rem,3.5vw,3.5rem)] pl-[2vw] pr-[clamp(2vw,6vw,6vw)]">
          <div
            className="relative h-[72vh] max-h-[640px] w-full overflow-hidden rounded-2xl"
            data-image-inner
          >
            <Image
              src={image}
              alt={`${title} — ${category}`}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 50vw"
              priority={index === 0}
              quality={85}
            />
          </div>
        </div>
      </article>
    );
  }
);

ProductCard.displayName = "ProductCard";

export default ProductCard;